import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  User, MapPin, Phone, Mail, CreditCard, Building2, FileText, Bot,
  ChevronDown, ChevronUp,
} from 'lucide-react';
import { callDetailsAPI } from '../services/api';
import { CallDetailsInputField } from './call-details/CallDetailsInputField';
import { CallDetailsHeader } from './call-details/CallDetailsHeader';
import { CallDetailsFooter } from './call-details/CallDetailsFooter';
import { DraftsPanel } from './call-details/DraftsPanel';
import { TranscriptViewer } from './call-details/TranscriptViewer';

const AUTO_SAVE_INTERVAL = 30000;

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

const buildEmptyFormData = (aiAgentName = '', transcript = '', summary = '') => ({
  firstName: '', lastName: '',
  currentAddress: '', city: '', state: '', zip: '',
  homePhone: '', cellPhone: '', emailId: '',
  verizonAccountNo: '', verizonAccountName: '', hasVerizonAccount: 'yes', previousAccountDetails: '',
  bankName: '', hasBankAccount: 'yes',
  paymentMethod: 'bank', cardNumber: '', cardExpiry: '', cardCvv: '', cardType: 'visa',
  transferredByAgentName: aiAgentName || '',
  aiAgentComments: '',
  closerComments: '',
  callTranscript: transcript || '',
  callSummary: summary || '',
});

const mapDraftToFormData = (draft) => ({
  firstName: draft.first_name || '',
  lastName: draft.last_name || '',
  currentAddress: draft.current_address || '',
  city: draft.city || '',
  state: draft.state || '',
  zip: draft.zip || '',
  homePhone: draft.home_phone || '',
  cellPhone: draft.cell_phone || '',
  emailId: draft.email || '',
  verizonAccountNo: draft.verizon_account_no || '',
  verizonAccountName: draft.verizon_account_name || '',
  hasVerizonAccount: draft.has_verizon_account || 'yes',
  previousAccountDetails: draft.previous_account_details || '',
  bankName: draft.bank_name || '',
  hasBankAccount: 'yes',
  paymentMethod: draft.payment_method || 'bank',
  cardNumber: '',
  cardExpiry: '',
  cardCvv: '',
  cardType: draft.card_type || 'visa',
  transferredByAgentName: draft.transferred_by_agent || '',
  aiAgentComments: draft.ai_agent_comments || '',
  closerComments: draft.closer_comments || '',
  callTranscript: draft.call_transcript || '',
  callSummary: draft.call_summary || '',
});

const RADIO_OPTIONS_HAS_VERIZON = [
  { value: 'yes', label: 'Has Verizon Account' },
  { value: 'no', label: 'No Account' },
  { value: 'previous', label: 'Previous Account' },
];

const RADIO_OPTIONS_PAYMENT = [
  { value: 'bank', label: 'Bank Account' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'prepaid_card', label: 'Prepaid Card' },
];

const Radio = ({ name, value, checked, label, onChange }) => (
  <label className="flex items-center gap-2 cursor-pointer">
    <input
      type="radio"
      name={name}
      checked={checked}
      onChange={() => onChange(value)}
      className="w-4 h-4 text-teal-400"
      data-testid={`radio-${name}-${value}`}
    />
    <span className="text-gray-300 text-sm">{label}</span>
  </label>
);

const CallDetailsForm = ({
  callData = null,
  transcript = '',
  summary = '',
  aiAgentName = '',
  onSave,
  onClose,
  userId,
}) => {
  const [formData, setFormData] = useState(() => buildEmptyFormData(aiAgentName, transcript, summary));
  const [errors, setErrors] = useState({});

  const [saving, setSaving] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [saved, setSaved] = useState(false);
  const [savedMessage, setSavedMessage] = useState('');

  const [showSummary, setShowSummary] = useState(true);
  const [showDrafts, setShowDrafts] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [loadingDrafts, setLoadingDrafts] = useState(false);
  const [currentDraftId, setCurrentDraftId] = useState(null);

  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle');
  const autoSaveIntervalRef = useRef(null);
  const initialFormDataRef = useRef(null);

  // Auto-fill from call data if available
  useEffect(() => {
    if (callData) {
      setFormData((prev) => ({
        ...prev,
        transferredByAgentName: callData.agentName || aiAgentName || prev.transferredByAgentName,
        callTranscript: callData.transcript || transcript || prev.callTranscript,
        callSummary: callData.summary || summary || prev.callSummary,
      }));
    }
  }, [callData, aiAgentName, transcript, summary]);

  // Snapshot the initial form data once for diffing
  useEffect(() => {
    initialFormDataRef.current = JSON.stringify(formData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track unsaved changes
  useEffect(() => {
    if (!initialFormDataRef.current) {
      setHasUnsavedChanges(false);
      return;
    }
    const changed = JSON.stringify(formData) !== initialFormDataRef.current;
    const hasContent =
      formData.firstName || formData.lastName || formData.cellPhone ||
      formData.closerComments || formData.aiAgentComments;
    setHasUnsavedChanges(changed && Boolean(hasContent));
  }, [formData]);

  // Fetch drafts
  const fetchDrafts = useCallback(async () => {
    if (!userId) return;
    setLoadingDrafts(true);
    try {
      const response = await callDetailsAPI.getDrafts(userId);
      if (response.data?.success) setDrafts(response.data.drafts || []);
    } catch (error) {
      console.error('Error fetching drafts:', error);
    } finally {
      setLoadingDrafts(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) fetchDrafts();
  }, [userId, fetchDrafts]);

  // Auto-save logic
  const performAutoSave = useCallback(async () => {
    if (!autoSaveEnabled || !hasUnsavedChanges || !userId) return;
    if (saving || savingDraft) return;

    setAutoSaveStatus('saving');
    try {
      const draftData = { ...formData, status: 'draft' };
      let ok = false;

      if (currentDraftId) {
        const response = await callDetailsAPI.update(currentDraftId, draftData, userId);
        ok = response.status === 200;
      } else if (onSave) {
        const result = await onSave({ ...formData, status: 'draft' });
        ok = Boolean(result);
        if (ok) fetchDrafts();
      }

      if (ok) {
        setAutoSaveStatus('saved');
        setHasUnsavedChanges(false);
        initialFormDataRef.current = JSON.stringify(formData);
      } else {
        setAutoSaveStatus('error');
      }
    } catch (error) {
      console.error('Auto-save error:', error);
      setAutoSaveStatus('error');
    }
    setTimeout(() => setAutoSaveStatus((s) => (s === 'error' ? 'error' : 'idle')), 3000);
  }, [
    autoSaveEnabled, hasUnsavedChanges, userId, formData, currentDraftId,
    saving, savingDraft, onSave, fetchDrafts,
  ]);

  useEffect(() => {
    if (autoSaveEnabled && userId) {
      autoSaveIntervalRef.current = setInterval(performAutoSave, AUTO_SAVE_INTERVAL);
    }
    return () => {
      if (autoSaveIntervalRef.current) clearInterval(autoSaveIntervalRef.current);
    };
  }, [autoSaveEnabled, userId, performAutoSave]);

  const handleChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => (prev[field] ? { ...prev, [field]: null } : prev));
  }, []);

  // Extract data from transcript
  const extractFromTranscript = () => {
    if (!formData.callTranscript) return;
    const text = formData.callTranscript.toLowerCase();

    const phoneMatch = text.match(/(\d{3}[-.\s]?\d{3}[-.\s]?\d{4})/g);
    const emailMatch = text.match(/([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+)/i);
    const zipMatch = text.match(/\b(\d{5}(?:-\d{4})?)\b/);

    setFormData((prev) => ({
      ...prev,
      cellPhone: prev.cellPhone || (phoneMatch ? phoneMatch[0] : ''),
      emailId: prev.emailId || (emailMatch ? emailMatch[1] : ''),
      zip: prev.zip || (zipMatch ? zipMatch[1] : ''),
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.cellPhone.trim()) newErrors.cellPhone = 'Cell phone is required';

    if (formData.paymentMethod !== 'bank' && formData.cardNumber) {
      const cardLength = formData.cardNumber.replace(/\D/g, '').length;
      const expectedLength = formData.cardType === 'amex' ? 15 : 16;
      if (cardLength > 0 && cardLength !== expectedLength) {
        newErrors.cardNumber = `Card number should be ${expectedLength} digits${
          formData.cardType === 'amex' ? ' for Amex' : ''
        }`;
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const flashSaved = (message) => {
    setSaved(true);
    setSavedMessage(message);
    setTimeout(() => { setSaved(false); setSavedMessage(''); }, 3000);
  };

  const handleSaveAsDraft = async () => {
    setSavingDraft(true);
    try {
      const draftData = { ...formData, status: 'draft' };
      if (currentDraftId) {
        const response = await callDetailsAPI.update(currentDraftId, draftData, userId);
        if (response.status === 200) {
          flashSaved('Draft updated!');
          setHasUnsavedChanges(false);
          initialFormDataRef.current = JSON.stringify(formData);
          fetchDrafts();
        }
      } else if (onSave) {
        await onSave(draftData);
        flashSaved('Saved as draft!');
        setHasUnsavedChanges(false);
        initialFormDataRef.current = JSON.stringify(formData);
        fetchDrafts();
      }
    } catch (error) {
      console.error('Error saving draft:', error);
    } finally {
      setSavingDraft(false);
    }
  };

  const loadDraft = (draft) => {
    const next = mapDraftToFormData(draft);
    setFormData(next);
    setCurrentDraftId(draft.id);
    setShowDrafts(false);
    setHasUnsavedChanges(false);
    setAutoSaveStatus('idle');
    initialFormDataRef.current = JSON.stringify(next);
  };

  const clearForm = useCallback(() => {
    const next = buildEmptyFormData(aiAgentName, transcript, summary);
    setFormData(next);
    setCurrentDraftId(null);
    setErrors({});
    setHasUnsavedChanges(false);
    setAutoSaveStatus('idle');
    initialFormDataRef.current = JSON.stringify(next);
  }, [aiAgentName, transcript, summary]);

  const deleteDraft = async (draftId, e) => {
    e.stopPropagation();
    try {
      await callDetailsAPI.delete(draftId, userId);
      fetchDrafts();
      if (currentDraftId === draftId) clearForm();
    } catch (error) {
      console.error('Error deleting draft:', error);
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      const saveData = { ...formData, status: 'captured' };
      if (currentDraftId) {
        const response = await callDetailsAPI.update(currentDraftId, saveData, userId);
        if (response.status === 200) {
          flashSaved('Saved successfully!');
          setCurrentDraftId(null);
          clearForm();
          fetchDrafts();
        }
      } else if (onSave) {
        await onSave(saveData);
        flashSaved('Saved successfully!');
        clearForm();
      }
    } catch (error) {
      console.error('Error saving form:', error);
    } finally {
      setSaving(false);
    }
  };

  // Small bound wrapper so all section inputs can share one prop signature
  const inputProps = (field) => ({
    field,
    value: formData[field],
    error: errors[field],
    cardType: formData.cardType,
    onChange: handleChange,
  });

  const isCardPayment = formData.paymentMethod === 'credit_card' || formData.paymentMethod === 'prepaid_card';

  return (
    <div className="bg-[#0d1117] border border-gray-800 rounded-2xl overflow-hidden" data-testid="call-details-form">
      <CallDetailsHeader
        currentDraftId={currentDraftId}
        autoSaveEnabled={autoSaveEnabled}
        autoSaveStatus={autoSaveStatus}
        hasUnsavedChanges={hasUnsavedChanges}
        showDrafts={showDrafts}
        draftsCount={drafts.length}
        onToggleAutoSave={() => setAutoSaveEnabled((v) => !v)}
        onToggleDrafts={() => {
          setShowDrafts((v) => {
            if (!v) fetchDrafts();
            return !v;
          });
        }}
        onClearForm={clearForm}
        onClose={onClose}
      />

      {showDrafts && (
        <DraftsPanel
          drafts={drafts}
          loading={loadingDrafts}
          currentDraftId={currentDraftId}
          onRefresh={fetchDrafts}
          onLoad={loadDraft}
          onDelete={deleteDraft}
        />
      )}

      <div className="p-6 max-h-[70vh] overflow-y-auto">
        {/* AI Summary */}
        {formData.callSummary && (
          <div className="mb-6">
            <button
              onClick={() => setShowSummary((v) => !v)}
              className="w-full flex items-center justify-between p-4 bg-teal-500/10 border border-teal-500/30 rounded-xl"
              data-testid="ai-summary-toggle-btn"
            >
              <div className="flex items-center gap-3">
                <Bot className="w-5 h-5 text-teal-400" />
                <span className="text-teal-400 font-medium">AI Call Summary</span>
              </div>
              {showSummary ? <ChevronUp className="w-5 h-5 text-teal-400" /> : <ChevronDown className="w-5 h-5 text-teal-400" />}
            </button>
            {showSummary && (
              <div className="mt-2 p-4 bg-[#1a1a1f] rounded-xl border border-gray-700">
                <p className="text-gray-300 text-sm whitespace-pre-wrap">{formData.callSummary}</p>
                <button
                  onClick={extractFromTranscript}
                  className="mt-3 text-teal-400 text-sm hover:underline flex items-center gap-1"
                  data-testid="autofill-from-transcript-btn"
                >
                  <FileText className="w-4 h-4" />
                  Auto-fill from transcript
                </button>
              </div>
            )}
          </div>
        )}

        {/* Personal Information */}
        <div className="mb-6">
          <h3 className="text-white font-medium mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-blue-400" />
            Personal Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <CallDetailsInputField label="First Name" placeholder="John" required icon={User} {...inputProps('firstName')} />
            <CallDetailsInputField label="Last Name" placeholder="Doe" required icon={User} {...inputProps('lastName')} />
          </div>
        </div>

        {/* Address */}
        <div className="mb-6">
          <h3 className="text-white font-medium mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-green-400" />
            Address
          </h3>
          <div className="space-y-4">
            <CallDetailsInputField label="Current Address" placeholder="123 Main St, Apt 4B" icon={MapPin} {...inputProps('currentAddress')} />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="col-span-2">
                <CallDetailsInputField label="City" placeholder="New York" {...inputProps('city')} />
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-1.5">State</label>
                <select
                  value={formData.state}
                  onChange={(e) => handleChange('state', e.target.value)}
                  className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                  data-testid="call-details-input-state"
                >
                  <option value="">Select</option>
                  {US_STATES.map((s) => (<option key={s} value={s}>{s}</option>))}
                </select>
              </div>
              <CallDetailsInputField label="Zip Code" placeholder="10001" maxLength={10} {...inputProps('zip')} />
            </div>
          </div>
        </div>

        {/* Contact */}
        <div className="mb-6">
          <h3 className="text-white font-medium mb-4 flex items-center gap-2">
            <Phone className="w-5 h-5 text-yellow-400" />
            Contact Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <CallDetailsInputField label="Home Phone" placeholder="(555) 123-4567" icon={Phone} format="phone" {...inputProps('homePhone')} />
            <CallDetailsInputField label="Cell Phone" placeholder="(555) 987-6543" required icon={Phone} format="phone" {...inputProps('cellPhone')} />
            <CallDetailsInputField label="Email ID" type="email" placeholder="john@example.com" icon={Mail} {...inputProps('emailId')} />
          </div>
        </div>

        {/* Verizon Account */}
        <div className="mb-6">
          <h3 className="text-white font-medium mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-red-400" />
            Verizon Account Information
          </h3>
          <div className="space-y-4">
            <div className="flex gap-4 flex-wrap">
              {RADIO_OPTIONS_HAS_VERIZON.map((opt) => (
                <Radio
                  key={opt.value}
                  name="hasVerizonAccount"
                  value={opt.value}
                  checked={formData.hasVerizonAccount === opt.value}
                  label={opt.label}
                  onChange={(v) => handleChange('hasVerizonAccount', v)}
                />
              ))}
            </div>
            {formData.hasVerizonAccount === 'yes' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <CallDetailsInputField label="Account Number" placeholder="Enter Verizon account number" {...inputProps('verizonAccountNo')} />
                <CallDetailsInputField label="Account Name" placeholder="Name on account" {...inputProps('verizonAccountName')} />
              </div>
            )}
            {(formData.hasVerizonAccount === 'no' || formData.hasVerizonAccount === 'previous') && (
              <div>
                <label className="block text-gray-400 text-sm mb-1.5">
                  {formData.hasVerizonAccount === 'previous' ? 'Previous Account Details' : 'Notes (No Verizon Account)'}
                </label>
                <textarea
                  value={formData.previousAccountDetails}
                  onChange={(e) => handleChange('previousAccountDetails', e.target.value)}
                  placeholder={formData.hasVerizonAccount === 'previous'
                    ? 'Enter previous account details...'
                    : 'Customer does not have a Verizon account...'}
                  className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white placeholder-gray-600 outline-none focus:border-teal-400 min-h-[80px]"
                  data-testid="call-details-input-previousAccountDetails"
                />
              </div>
            )}
          </div>
        </div>

        {/* Payment */}
        <div className="mb-6">
          <h3 className="text-white font-medium mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-purple-400" />
            Payment Information
          </h3>
          <div className="space-y-4">
            <div className="flex gap-4 flex-wrap">
              {RADIO_OPTIONS_PAYMENT.map((opt) => (
                <Radio
                  key={opt.value}
                  name="paymentMethod"
                  value={opt.value}
                  checked={formData.paymentMethod === opt.value}
                  label={opt.label}
                  onChange={(v) => handleChange('paymentMethod', v)}
                />
              ))}
            </div>
            {formData.paymentMethod === 'bank' && (
              <CallDetailsInputField label="Bank Name" placeholder="Enter bank name" icon={Building2} {...inputProps('bankName')} />
            )}
            {isCardPayment && (
              <div className="space-y-4">
                <div className="flex gap-4 flex-wrap">
                  {['visa', 'mastercard', 'amex', 'other'].map((type) => (
                    <Radio
                      key={type}
                      name="cardType"
                      value={type}
                      checked={formData.cardType === type}
                      label={type === 'amex' ? 'Amex (15 digits)' : type.charAt(0).toUpperCase() + type.slice(1)}
                      onChange={(v) => handleChange('cardType', v)}
                    />
                  ))}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-1">
                    <CallDetailsInputField
                      label={`Card Number (${formData.cardType === 'amex' ? '15' : '16'} digits)`}
                      placeholder={formData.cardType === 'amex' ? '3782 822463 10005' : '4111 1111 1111 1111'}
                      icon={CreditCard}
                      format="card"
                      {...inputProps('cardNumber')}
                    />
                  </div>
                  <CallDetailsInputField label="Expiry (MM/YY)" placeholder="12/25" maxLength={5} {...inputProps('cardExpiry')} />
                  <CallDetailsInputField
                    label="CVV"
                    placeholder={formData.cardType === 'amex' ? '1234' : '123'}
                    maxLength={formData.cardType === 'amex' ? 4 : 3}
                    {...inputProps('cardCvv')}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Call Info */}
        <div className="mb-6">
          <h3 className="text-white font-medium mb-4 flex items-center gap-2">
            <Bot className="w-5 h-5 text-teal-400" />
            Call Information
          </h3>
          <div className="space-y-4">
            <CallDetailsInputField
              label="Call Transferred by Agent Name"
              placeholder="AI Fraud Detection Agent"
              icon={Bot}
              {...inputProps('transferredByAgentName')}
            />
            <div>
              <label className="block text-gray-400 text-sm mb-1.5">AI Agent Comments</label>
              <textarea
                value={formData.aiAgentComments}
                onChange={(e) => handleChange('aiAgentComments', e.target.value)}
                placeholder="Notes from AI agent about the call..."
                className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white placeholder-gray-600 outline-none focus:border-teal-400 min-h-[80px]"
                data-testid="call-details-input-aiAgentComments"
              />
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-1.5">Closer Comments</label>
              <textarea
                value={formData.closerComments}
                onChange={(e) => handleChange('closerComments', e.target.value)}
                placeholder="Your notes and comments about the call resolution..."
                className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white placeholder-gray-600 outline-none focus:border-teal-400 min-h-[100px]"
                data-testid="call-details-input-closerComments"
              />
            </div>
          </div>
        </div>

        <TranscriptViewer transcript={formData.callTranscript} />
      </div>

      <CallDetailsFooter
        saved={saved}
        savedMessage={savedMessage}
        saving={saving}
        savingDraft={savingDraft}
        currentDraftId={currentDraftId}
        onSave={handleSave}
        onSaveAsDraft={handleSaveAsDraft}
        onClose={onClose}
      />
    </div>
  );
};

export default CallDetailsForm;
